import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { useCurrency } from "@/context/CurrencyContext";
import BudgetFitChip from "@/components/app/BudgetFitChip";
import { Wallet, TrendingDown, TrendingUp } from "lucide-react";
import { trackDebounced, EVENTS } from "@/lib/track";

// Live budget dashboard on top of the Shortlist page. Reads the user's
// stored `profile.total_family_budget_usd` (or falls back to $120k),
// sums the 3-year net cost of every pinned degree, and shows how many
// fit — plus min / max / average per-year tuition. Non-destructive:
// nothing is hidden or re-ordered; each shortlist row picks up a
// fits/over chip via the shared `BudgetFitChip` component.
//
// Props:
//   items     — shortlist rows (from GET /api/shortlist)
//   onBudget  — (optional) called with the current envelope so a parent
//               can render fits-budget chips on the shortlist rows.

const BUDGET_DEFAULT = 120000;
const BUDGET_MIN = 30000;
const BUDGET_MAX = 300000;
const BUDGET_STEP = 5000;

// Country-string buckets so a profile set to "UK" matches a uni with
// `country: "United Kingdom"` and vice-versa. Keep narrow — we only
// need the strings our ingestion pipelines actually emit.
const COUNTRY_ALIASES = {
  UK: new Set(["UK", "United Kingdom", "GB", "England", "Scotland", "Wales", "Northern Ireland"]),
  US: new Set(["US", "USA", "United States", "United States of America"]),
};

// Map a uni's `country` string to one of the engine's target buckets
// (/api/fee-status/me → by_country keys). Returns null for a country
// we don't model yet — the caller falls back to raw-rate logic.
function _countryBucket(uniCountry) {
  if (!uniCountry) return null;
  const c = String(uniCountry).trim();
  if (COUNTRY_ALIASES.UK.has(c)) return "UK";
  if (COUNTRY_ALIASES.US.has(c)) return "US";
  const map = {
    Ireland: "IE", France: "FR", Germany: "DE", Deutschland: "DE",
    Netherlands: "NL", "The Netherlands": "NL",
    Canada: "CA", Australia: "AU",
    Singapore: "SG",
    "United Arab Emirates": "AE", UAE: "AE", Emirates: "AE",
    "Hong Kong": "HK", "Hong Kong SAR": "HK",
    China: "CN", "People's Republic of China": "CN",
    India: "IN", Japan: "JP",
  };
  if (map[c]) return map[c];
  // ISO-2 fallbacks for anywhere our ingestion already stores codes.
  if (/^[A-Z]{2}$/.test(c)) return c;
  return null;
}

// Statutory home-fee fallbacks in USD, applied when a course is
// inferred as "home" by the engine but the DB has no domestic rate
// on file for that specific course. These are public-policy numbers,
// not evidence-dependent, so they're a safe sticker price.
const HOME_FEE_CAP_USD = {
  UK: 12764,    //  £10,050 · 2027/28 GOV.UK cap
  IE:  3000,    //  €3,000 Student Contribution
  FR:   250,    //  ~€175 statutory
  DE:   350,    //  semester admin (tuition is free)
  NL:  2700,    //  €2,530 statutory
  US: 12000,    //  in-state proxy
  CA:  8500,    //  CA$12,000 → USD
  AU: 10500,    //  A$15,000 CSP ceiling → USD
  SG:  8000,    //  Tier A citizen rate (humanities proxy)
  AE:     0,    //  Emirati at federal uni
  HK:  5400,    //  HK$42,100
  CN:   800,    //  ¥6,000
  IN:   600,    //  ₹50,000
  JP:  3600,    //  ¥535,800 national-uni standard
};

function _tuitionFor(it, feeStatusByCountry) {
  const d = it?.degree;
  if (!d?.cost) return 0;
  const bucket = _countryBucket(it?.university?.country);
  const dom  = Number(d.cost.tuition_domestic_annual_usd || 0);
  const intl = Number(d.cost.tuition_intl_annual_usd || 0);
  // When we have a modelled bucket + engine result, pick strictly on
  // inferred status. Borderline is treated as international so the
  // dashboard errs on the side of a bigger (more realistic) number.
  const inferred = bucket && feeStatusByCountry?.[bucket];
  if (inferred?.status === "home") {
    if (dom) return dom;
    if (bucket in HOME_FEE_CAP_USD) return HOME_FEE_CAP_USD[bucket];
    if (intl) return intl;
    return 0;
  }
  if (inferred?.status === "international" || inferred?.status === "borderline") {
    return intl || dom;
  }
  // Unknown / unmodelled target → fall back to whichever rate exists,
  // preferring intl (safer upper bound).
  return intl || dom;
}

function _totalCostOf(it, feeStatusByCountry) {
  const tuition = _tuitionFor(it, feeStatusByCountry);
  if (!tuition) return 0;
  const dur = Number(it?.degree?.duration_years || 3);
  // Approximate 3-year net cost like advanced_engine._financial_fit does:
  // tuition × duration + a $12k/year living-cost baseline.
  return Math.round(tuition * dur + 12000 * dur);
}

export default function ShortlistBudgetSummary({ items, onBudget }) {
  const { format } = useCurrency();
  const [profile, setProfile] = useState(null);
  const [feeStatus, setFeeStatus] = useState(null);  // {UK: {status,…}, IE: {…}}
  const [envelope, setEnvelope] = useState(BUDGET_DEFAULT);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/profile");
        if (cancelled) return;
        setProfile(data || {});
        const stored = Number(data?.total_family_budget_usd);
        if (stored > 0) setEnvelope(stored);
      } catch { /* unauthenticated or no profile — keep default */ }
    })();
    // Fee-status map from the engine. Independent of /me/profile — the
    // profile call carries the raw fields; this one carries the
    // *inferred* status per target country used by `_tuitionFor`.
    (async () => {
      try {
        const { data } = await api.get("/fee-status/me");
        if (!cancelled) setFeeStatus(data?.by_country || null);
      } catch { /* happy path — fall back to intl everywhere */ }
    })();
    const onProfileSaved = () => {
      api.get("/fee-status/me")
        .then(r => setFeeStatus(r.data?.by_country || null))
        .catch(() => {});
    };
    window.addEventListener("wua:profile-updated", onProfileSaved);
    return () => {
      cancelled = true;
      window.removeEventListener("wua:profile-updated", onProfileSaved);
    };
  }, []);

  useEffect(() => {
    if (typeof onBudget === "function") onBudget(envelope);
  }, [envelope, onBudget]);

  const stats = useMemo(() => {
    const rows = (items || []).filter((it) => it?.degree);
    const costs = rows.map((it) => _totalCostOf(it, feeStatus)).filter((c) => c > 0);
    const tuitions = rows
      .map((it) => _tuitionFor(it, feeStatus))
      .filter((c) => c > 0);
    // Count each course by the fee tier it ended up on so we can
    // disclose a "N home · M international" summary line.
    const homeCount = rows.filter((it) => {
      const bucket = _countryBucket(it?.university?.country);
      return bucket && feeStatus?.[bucket]?.status === "home"
             && _tuitionFor(it, feeStatus) > 0;
    }).length;
    // How many landed on the statutory-cap fallback (no cost on file
    // but engine said "home") — triggers the "using country X cap"
    // transparency line.
    const cappedCount = rows.filter((it) => {
      const d = it?.degree;
      if (!d?.cost) return false;
      const bucket = _countryBucket(it?.university?.country);
      if (!bucket || !(bucket in HOME_FEE_CAP_USD)) return false;
      if (feeStatus?.[bucket]?.status !== "home") return false;
      return !(d.cost.tuition_domestic_annual_usd || 0);
    }).length;
    const borderlineCount = rows.filter((it) => {
      const bucket = _countryBucket(it?.university?.country);
      return bucket && feeStatus?.[bucket]?.status === "borderline";
    }).length;
    const sorted = [...costs].sort((a, b) => a - b);
    const medianCost = sorted.length === 0
      ? 0
      : sorted.length % 2 === 1
        ? sorted[(sorted.length - 1) >> 1]
        : Math.round((sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2);
    return {
      count: rows.length,
      withCost: costs.length,
      missingCost: rows.length - costs.length,
      homeCount,
      cappedCount,
      borderlineCount,
      medianCost,
      minCost: costs.length ? Math.min(...costs) : 0,
      maxCost: costs.length ? Math.max(...costs) : 0,
      avgTuition: tuitions.length
        ? Math.round(tuitions.reduce((a, b) => a + b, 0) / tuitions.length)
        : 0,
      fits: costs.filter((c) => c <= envelope).length,
    };
  }, [items, envelope, feeStatus]);

  if (!items || items.length === 0) return null;
  if (stats.withCost === 0) return null;

  // The static BUDGET_MAX (= $300k) was too low for shortlists that
  // include a 4-yr US private or a high-tuition UK international
  // course — the slider literally couldn't reach the cheapest option,
  // so "0 of N fit" was unreachable at any drag position. Always keep
  // ≥10 % headroom above the priciest shortlisted course, rounded up
  // to the slider step so the handle snaps cleanly to the ceiling.
  const dynamicMax = Math.max(
    BUDGET_MAX,
    stats.maxCost
      ? Math.ceil((stats.maxCost * 1.1) / BUDGET_STEP) * BUDGET_STEP
      : BUDGET_MAX,
  );
  const clampedEnvelope = Math.min(envelope, dynamicMax);

  const pct = stats.withCost ? Math.round((stats.fits / stats.withCost) * 100) : 0;
  // Fit percentage is only meaningful against the courses we have
  // cost data for; the headline shows the full shortlist count so
  // users understand how many degrees are being evaluated vs skipped.
  const profileBudget = Number(profile?.total_family_budget_usd) || 0;
  const isDefault = envelope === (profileBudget > 0 ? profileBudget : BUDGET_DEFAULT);

  return (
    <div className="mt-6 rounded-lg border border-[#4C1D95]/25 bg-white overflow-hidden"
         data-testid="shortlist-budget-summary">
      <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(300px,1fr)] gap-4 px-5 py-4 items-center border-b border-[#4C1D95]/15">
        <div>
          <div className="flex items-center gap-2 label-uppercase text-[#4C1D95]">
            <Wallet className="h-3.5 w-3.5" />
            <span>Shortlist budget dashboard</span>
          </div>
          <div className="mt-1 font-editorial text-2xl leading-tight text-stone-900">
            <span data-testid="shortlist-budget-count">{stats.fits}</span>{" "}
            <span className="text-stone-500">of {stats.count}</span>{" "}
            <span className="text-stone-500 text-lg">fit your</span>{" "}
            <span className="text-[#4C1D95]" data-testid="shortlist-budget-value">
              {format(envelope)}
            </span>{" "}
            <span className="text-stone-500 text-lg">envelope</span>
          </div>
          <p className="text-[11px] text-stone-500 mt-1 leading-snug">
            Total 3-year cost estimate (
            {stats.homeCount === stats.withCost && stats.withCost > 0
              ? "home / domestic tuition"
              : stats.homeCount === 0
              ? "international tuition"
              : `${stats.homeCount} home · ${stats.withCost - stats.homeCount} international`}
            {" "}+ {format(12000)}/yr living costs). Drag to stress-test the shortlist.
            {!profile?.nationalities?.length && (items || []).length > 0 && (
              <>
                {" "}
                <Link
                  to="/profile#profile-field-nationalities"
                  data-testid="shortlist-budget-set-fee-status"
                  className="text-amber-700 underline underline-offset-2 hover:text-amber-900"
                >
                  Set your fee status on your profile
                </Link>
                <span className="text-amber-700"> to price home courses correctly for your passport.</span>
              </>
            )}
            {stats.cappedCount > 0 && (
              <>
                {" "}
                <span className="text-stone-500">
                  ({stats.cappedCount} home course{stats.cappedCount === 1 ? "" : "s"} using the country's statutory home-fee rate — no per-course fee on file.)
                </span>
              </>
            )}
            {stats.borderlineCount > 0 && (
              <>
                {" "}
                <span className="text-amber-700">
                  {stats.borderlineCount} course{stats.borderlineCount === 1 ? "" : "s"} on a borderline status — confirm with the uni.
                </span>
              </>
            )}
            {stats.missingCost > 0 && (
              <>
                {" "}
                <span data-testid="shortlist-budget-missing" className="text-amber-700">
                  {stats.missingCost} course{stats.missingCost === 1 ? "" : "s"} skipped — no tuition on file yet.
                </span>
              </>
            )}
          </p>
        </div>
        <div className="w-full">
          <div className="flex items-baseline justify-between text-[11px] text-stone-500 mb-2">
            <span>{format(BUDGET_MIN)}</span>
            <span className="font-mono text-[#4C1D95]">
              {pct}% fit{stats.missingCost > 0 ? " (of priced)" : ""}
            </span>
            <span>{format(dynamicMax)}</span>
          </div>
          <Slider
            data-testid="shortlist-budget-slider"
            min={BUDGET_MIN}
            max={dynamicMax}
            step={BUDGET_STEP}
            value={[clampedEnvelope]}
            onValueChange={(v) => {
              const next = Math.max(BUDGET_MIN, Math.min(dynamicMax, v[0]));
              setEnvelope(next);
              trackDebounced(EVENTS.BUDGET_SLIDER, {
                value: next,
                page: "/shortlist",
                fits: stats.fits,
                total: stats.withCost,
              });
            }}
            className="mt-1"
          />
          <div className="mt-2 flex items-center justify-end">
            <button
              type="button"
              data-testid="shortlist-budget-reset"
              onClick={() => setEnvelope(profileBudget > 0 ? profileBudget : BUDGET_DEFAULT)}
              disabled={isDefault}
              className="text-[10px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline disabled:text-stone-300 disabled:no-underline"
            >
              Reset to default
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#4C1D95]/15">
        <StatCell
          label="Cheapest 3-yr"
          value={format(stats.minCost)}
          icon={<TrendingDown className="h-3 w-3 text-emerald-700" />}
          testid="shortlist-budget-min"
        />
        <StatCell
          label="Priciest 3-yr"
          value={format(stats.maxCost)}
          icon={<TrendingUp className="h-3 w-3 text-amber-700" />}
          testid="shortlist-budget-max"
        />
        <StatCell
          label="Average tuition /yr"
          value={format(stats.avgTuition)}
          testid="shortlist-budget-avg"
        />
        <StatCell
          label="Median 3-yr cost"
          value={format(stats.medianCost)}
          testid="shortlist-budget-median"
          hint="Half your shortlist costs this or less across the full degree. A better realistic-outcome number than the priciest option alone."
        />
      </div>
    </div>
  );
}

function StatCell({ label, value, icon, testid, hint }) {
  return (
    <div className="px-4 py-3" data-testid={testid} title={hint}>
      <div className="flex items-center gap-1 label-uppercase !text-[9px] text-stone-500">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-0.5 font-mono text-sm text-stone-900">{value}</div>
    </div>
  );
}

// Convenience helper for parents that want to compute the per-row total
// cost with the same formula the summary uses (tuition × duration + $12k/yr
// living costs). Exported so the Shortlist page can render `BudgetFitChip`
// on each row consistently. `feeStatusByCountry` is optional — pass the
// `by_country` map from GET /fee-status/me to price home courses correctly
// for the signed-in student; omit it to default to the international rate.
export function estimateShortlistTotalCost(item, feeStatusByCountry) {
  return _totalCostOf(item, feeStatusByCountry);
}
